import { useState } from 'react';
import { Table, Select, Button, Tooltip, Empty, App, theme, type TableColumnsType } from 'antd';
import { LayoutOutlined, AppstoreOutlined, CheckCircleOutlined, MinusCircleOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useSectionBoard, setSectionTopics, useDisplaySections, type BoardTopic } from '@/api/displaySections';
import { useTopics } from '@/hooks/useTopics';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';

export function HomeSectionsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { token } = theme.useToken();
  const boardQ = useSectionBoard();
  const secQ = useDisplaySections();
  const activeQ = useTopics({ status: 'active' });
  const [dragKey, setDragKey] = useState<number | null>(null);

  const board = boardQ.data ?? [];
  const placed = new Set<number>();
  board.forEach((s) => s.topics.forEach((t) => placed.add(t.id)));
  const activeCount = (activeQ.data ?? []).length;
  const unplaced = (activeQ.data ?? []).filter((t) => !placed.has(Number(t.id))).length;

  async function apply(sectionId: number, topicIds: number[]) {
    try {
      await setSectionTopics(sectionId, topicIds);
      qc.invalidateQueries({ queryKey: ['section-board'] });
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function columnsFor(sectionId: number, topics: BoardTopic[]): TableColumnsType<BoardTopic> {
    return [
      { title: '', key: 'drag', width: 30, render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} /> },
      { title: 'Title', dataIndex: 'title', render: (v) => <strong>{v}</strong> },
      { title: 'Topic', dataIndex: 'topic_no', width: 140, render: (v) => <code>{v}</code> },
      {
        title: '', width: 44,
        render: (_, r) => (
          <Tooltip title="Remove">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => apply(sectionId, topics.filter((t) => t.id !== r.id).map((t) => t.id))} />
          </Tooltip>
        ),
      },
    ];
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <PageHero
        icon={<LayoutOutlined />}
        title="Home layout"
        subtitle="Arrange which topics appear in each home section and in what order"
        from="#e11d48" to="#f43f5e"
        chips={[
          <HeroChip key="sec">Sections {board.length}</HeroChip>,
          <HeroChip key="placed">Placed {placed.size}</HeroChip>,
        ]}
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<AppstoreOutlined />} label="Sections" value={board.length} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Placed topics" value={placed.size} />
        <KpiTile tone="amber" icon={<MinusCircleOutlined />} label="Unplaced active" value={unplaced} />
        <KpiTile tone="indigo" icon={<CheckCircleOutlined />} label="Active topics" value={activeCount} />
      </div>

      {board.map((section) => {
        const sec = secQ.data?.find((s) => s.id === section.id);
        const inIds = new Set(section.topics.map((t) => t.id));
        const addable = (activeQ.data ?? [])
          .filter((t) => (sec?.allowed_type_ids.includes(t.type_id) ?? false) && !inIds.has(Number(t.id)))
          .map((t) => ({ value: Number(t.id), label: t.details.title?.en ?? `(untitled) ${t.id}` }));
        return (
          <SectionCard key={section.id} tone="indigo" icon={<LayoutOutlined />} title={section.tag_name} desc={`${section.topics.length} topic(s)`}>
            <div style={{ marginBottom: 12 }}>
              <Select
                showSearch
                size="small"
                style={{ minWidth: 320 }}
                placeholder="Add a permitted active topic…"
                value={null}
                options={addable}
                filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={(v) => v != null && apply(section.id, [...section.topics.map((t) => t.id), Number(v)])}
                notFoundContent="No eligible topics"
              />
            </div>
            <Table<BoardTopic>
              rowKey="id"
              size="small"
              pagination={false}
              loading={boardQ.isLoading}
              columns={columnsFor(section.id, section.topics)}
              dataSource={section.topics}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No topics in this section" /> }}
              onRow={(record, index) => ({
                draggable: true,
                onDragStart: () => setDragKey(record.id),
                onDragOver: (e) => e.preventDefault(),
                onDrop: () => {
                  if (dragKey != null && dragKey !== record.id && index !== undefined) {
                    const ids = section.topics.map((t) => t.id).filter((x) => x !== dragKey);
                    ids.splice(index, 0, dragKey);
                    apply(section.id, ids);
                  }
                  setDragKey(null);
                },
              })}
              style={{ background: token.colorBgContainer }}
            />
          </SectionCard>
        );
      })}
    </div>
  );
}
